/**
 * Pull the human-readable message out of an axios-style error.
 *
 * crm-api returns failures as a structured body — either `{ errors: [".."] }`
 * (the Rails `unprocessable_entity` / validation shape) or `{ error: ".." }`.
 * axios, however, only puts a generic "Request failed with status code N" on
 * `error.message`, so naively surfacing `error.message` throws away the real
 * reason the server gave us. (That is exactly why "Send test email" showed a
 * useless "Request failed with status code 422" instead of, e.g., the SMTP
 * failure detail.)
 *
 * Preference order: first string in `data.errors` → `data.error` →
 * `error.message` → `fallback`. Mirrors the inline idiom used across the CRM
 * components (e.g. link-customer-to-reseller-modal, reseller-dealers-list).
 */
export function extractApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  const data = (
    error as { response?: { data?: { errors?: unknown; error?: unknown } } } | undefined
  )?.response?.data

  if (Array.isArray(data?.errors)) {
    const first = data.errors.find(
      (entry): entry is string => typeof entry === "string" && entry.trim().length > 0,
    )
    if (first) return first
  }

  if (typeof data?.error === "string" && data.error.trim().length > 0) {
    return data.error
  }

  const message = (error as { message?: unknown } | undefined)?.message
  if (typeof message === "string" && message.trim().length > 0) {
    return message
  }

  return fallback
}

/**
 * Richer cousin of {@link extractApiErrorMessage} for call sites that want a
 * different precedence than first-error-wins. It prefers `data.error` then
 * `data.message`, JOINS *all* non-blank entries in `data.errors` (rather than
 * returning only the first), then `error.message`, then `fallback` — and
 * finally replaces a present-but-empty result with `blankFallback`.
 *
 * This is the exact idiom the File Manager page repeated inline at NINE catch
 * sites; lifting it here removes that duplication verbatim. It is deliberately
 * SEPARATE from `extractApiErrorMessage`: that one is first-error-only and is
 * relied on by other CRM callers, so its behavior must not change.
 *
 * `errorsEmptyFallback` (used when `data.errors` holds only blanks) and
 * `blankFallback` (the present-but-empty guard) both default to `fallback`.
 */
export function composeApiErrorMessage(
  error: any,
  opts: { fallback: string; errorsEmptyFallback?: string; blankFallback?: string },
): string {
  const fallback = opts.fallback
  const errorsEmptyFallback = opts.errorsEmptyFallback ?? fallback
  const blankFallback = opts.blankFallback ?? fallback

  let message: string = fallback
  if (error?.response?.data?.error) {
    message = error.response.data.error
  } else if (error?.response?.data?.message) {
    message = error.response.data.message
  } else if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    const validErrors = error.response.data.errors.filter(
      (e: any) => e && e.trim && e.trim() !== "",
    )
    message = validErrors.length > 0 ? validErrors.join(", ") : errorsEmptyFallback
  } else if (error?.message) {
    message = error.message
  }

  if (!message || message.trim() === "") {
    message = blankFallback
  }

  return message
}

export default extractApiErrorMessage
